import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { classNames, truncateToWords } from '@/lib/format';
import { StarRating } from '@/components/StarRating';

const THUMB_SEEK_SEC = 300;

/**
 * Video card with frame thumbnail, hover muted preview, and click-to-open modal.
 * Pass `mediaKey` (e.g. question_id) to lock src after first successful load so
 * parent re-renders with fresh presigned URLs do not reload the element.
 */
export function VideoPreviewCard({
  videoUrl,
  mediaKey,
  title,
  durationSec,
  starScore,
  summary,
  badge,
  onOpen,
  className,
  testId = 'video-preview-card',
  heightClass = 'aspect-[4/3]',
}) {
  const videoRef = useRef(null);
  const lockedRef = useRef({ key: null, src: null });
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hovering, setHovering] = useState(false);

  const stableKey = mediaKey || videoUrl || null;
  const playbackSrc = (stableKey && lockedRef.current.key === stableKey && lockedRef.current.src)
    ? lockedRef.current.src
    : videoUrl;

  useEffect(() => {
    if (lockedRef.current.key !== stableKey) {
      lockedRef.current = { key: stableKey, src: null };
      setReady(false);
      setFailed(false);
    }
  }, [stableKey]);

  const seekThumbnail = useCallback((video) => {
    if (!video || !video.duration || Number.isNaN(video.duration)) return;
    const t = Math.min(THUMB_SEEK_SEC, Math.max(0, video.duration - 0.1));
    try {
      video.currentTime = t;
    } catch {
      /* ignore seek errors on some browsers */
    }
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (stableKey && video.src) {
      lockedRef.current = { key: stableKey, src: video.src };
    }
    seekThumbnail(video);
    video.pause();
    setReady(true);
  }, [seekThumbnail, stableKey]);

  const onMouseEnter = useCallback(() => {
    setHovering(true);
    const video = videoRef.current;
    if (!video || !playbackSrc || failed) return;
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, [playbackSrc, failed]);

  const onMouseLeave = useCallback(() => {
    setHovering(false);
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    seekThumbnail(video);
  }, [seekThumbnail]);

  const showFallback = !playbackSrc || failed;
  const durationLabel = durationSec != null ? `${Math.round(durationSec)}s` : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={testId}
      className={classNames(
        'group block w-full overflow-hidden rounded-xl border bg-card text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className,
      )}
    >
      <div className={classNames('relative w-full bg-gradient-to-br from-slate-100 to-slate-200', heightClass)}>
        {playbackSrc && !failed && (
          <video
            ref={videoRef}
            src={playbackSrc}
            preload="metadata"
            muted
            playsInline
            className={classNames(
              'absolute inset-0 h-full w-full object-cover',
              !ready && 'opacity-0',
            )}
            onLoadedMetadata={onLoadedMetadata}
            onError={() => setFailed(true)}
          />
        )}
        {showFallback && (
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white shadow-md transition-transform group-hover:scale-105">
              <Play className="h-4 w-4 translate-x-[1px] text-primary" />
            </span>
          </span>
        )}
        {!showFallback && !hovering && (
          <span className="pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover:opacity-100">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow-md">
              <Play className="h-4 w-4 translate-x-[1px] text-primary" />
            </span>
          </span>
        )}
        {badge && (
          <span className="absolute right-1.5 top-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {badge}
          </span>
        )}
        {durationLabel && (
          <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white tabular-nums">
            {durationLabel}
          </span>
        )}
      </div>
      <div className="space-y-1.5 p-2">
        {title && (
          <p className="line-clamp-2 text-[12px] font-medium leading-snug">{title}</p>
        )}
        {starScore != null && (
          <StarRating score={starScore} testId={`${testId}-star-rating`} />
        )}
        {summary && (
          <p className="text-[11px] leading-snug text-muted-foreground line-clamp-3">
            {truncateToWords(summary, 20)}
          </p>
        )}
      </div>
    </button>
  );
}
